import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { User, LoginCredentials, RegisterCredentials, LoginResult } from '../types';
import { refreshAccessToken, clearAuthData, checkAndRefreshToken } from '../utils/auth';
import { STORAGE_KEYS } from '../constants';
import { appToast } from '../utils/toast';
import { getApiErrorMessage } from '../utils/errors';
import { useLanguage } from './LanguageContext';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  completeMfaLogin: (data: { mfa_token: string; otp_code: string; phone?: string }) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  loginWithFacebook: (accessToken: string) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  updateUser: (userData: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async (): Promise<void> => {
    try {
      const tokenValid = await checkAndRefreshToken();
      
      if (tokenValid) {
        // Try to get user data from API to ensure it's up to date
        try {
          const response = await authAPI.getCurrentUser();
          const userData = response.data.user;
          if (userData) {
            localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
            setUser(userData);
          } else {
            // Fallback to stored user data if API doesn't return user
            const storedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
            if (storedUserData) {
              setUser(JSON.parse(storedUserData));
            }
          }
        } catch (error) {
          // Fallback to stored user data if API call fails
          const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
          if (userData) {
            setUser(JSON.parse(userData));
          } else {
            clearAuthData();
            setUser(null);
          }
        }
      } else {
        clearAuthData();
        setUser(null);
      }
    } catch {
      clearAuthData();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const applyAuthData = (data: any): void => {
    const { access_token, refresh_token, user: userData } = data;
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    setUser(userData);
  };

  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    try {
      const response = await authAPI.login(credentials);
      const data = response.data;

      // Admins get a second-factor challenge instead of tokens.
      if (data?.mfa_required) {
        return {
          mfaRequired: true,
          mfaToken: data.mfa_token,
          needsPhone: !!data.needs_phone,
          phoneHint: data.phone_hint || '',
        };
      }

      applyAuthData(data);
      appToast.success(t.toasts.loginSuccess);
      return { mfaRequired: false };
    } catch (error: any) {
      // The Login page shows this error inline instead of as a transient toast,
      // so we just rethrow here (no toast) to avoid a duplicate message.
      throw error;
    }
  };

  const completeMfaLogin = async (
    data: { mfa_token: string; otp_code: string; phone?: string }
  ): Promise<void> => {
    const response = await authAPI.loginMfaVerify(data);
    applyAuthData(response.data);
    appToast.success(t.toasts.loginSuccess);
  };

  const loginWithGoogle = async (credential: string): Promise<void> => {
    try {
      const response = await authAPI.googleLogin(credential);
      const { access_token, refresh_token, user: userData } = response.data;

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
      setUser(userData);

      appToast.success(t.toasts.loginSuccess);
    } catch (error: any) {
      appToast.error(getApiErrorMessage(error, t.toasts.loginFailed, t.apiErrors));
      throw error;
    }
  };

  const loginWithFacebook = async (accessToken: string): Promise<void> => {
    try {
      const response = await authAPI.facebookLogin(accessToken);
      applyAuthData(response.data);
      appToast.success(t.toasts.loginSuccess);
    } catch (error: any) {
      appToast.error(getApiErrorMessage(error, t.toasts.loginFailed, t.apiErrors));
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials): Promise<void> => {
    try {
      const response = await authAPI.register(credentials);
      const { access_token, refresh_token, user: userData } = response.data;
      
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
      setUser(userData);
      
      appToast.success(t.toasts.registerSuccess);
    } catch (error: any) {
      // The Register page renders these errors inline (and localized), so we
      // rethrow without a toast to avoid a duplicate/English message.
      throw error;
    }
  };

  const logout = (): void => {
    clearAuthData();
    setUser(null);
    appToast.success(t.toasts.logoutSuccess);
  };

  const updateUser = (userData: User): void => {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    setUser(userData);
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const newAccessToken = await refreshAccessToken();
      
      if (newAccessToken) {
        return true;
      } else {
        appToast.error(t.toasts.tokenRefreshFailed);
        return false;
      }
    } catch (error: any) {
      appToast.error(getApiErrorMessage(error, t.toasts.tokenRefreshFailed, t.apiErrors));
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    completeMfaLogin,
    loginWithGoogle,
    loginWithFacebook,
    register,
    logout,
    refreshToken,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 